import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { Card } from '../components/ui/card';
import { Trash2, AlertTriangle, Loader2 } from 'lucide-react';
import { useAuth } from '../context/AuthContext';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function DeleteAccount() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { user, setUser } = useAuth();
  const [confirmed, setConfirmed] = useState(false);
  const [step, setStep] = useState(0); // 0 = page, 1 = 1er bandeau, 2 = 2e bandeau (définitif)
  const [busy, setBusy] = useState(false);

  const doDelete = () => {
    if (busy) return;
    setBusy(true);
    axios.delete(API_URL + '/api/auth/me', { withCredentials: true })
      .then(function () {
        setUser(null);
        toast.success(t('deleteAccount.success'));
        navigate('/');
      })
      .catch(function () {
        toast.error(t('deleteAccount.error'));
        setBusy(false);
        setStep(0);
      });
  };

  return (
    <div className="min-h-screen bg-[#050505] pt-24 pb-12">
      <div className="max-w-2xl mx-auto px-6">
        <h1 className="text-4xl sm:text-5xl font-bold text-white mb-8" style={{ fontFamily: 'Syne, sans-serif' }}>
          {t('deleteAccount.title')}
        </h1>

        <Card className="bg-[#F9004D]/10 border-[#F9004D]/50 p-6 mb-8">
          <div className="flex items-start gap-3">
            <AlertTriangle className="h-6 w-6 text-[#F9004D] flex-shrink-0 mt-1" />
            <p className="text-zinc-200 leading-relaxed">{t('deleteAccount.intro')}</p>
          </div>
        </Card>

        <Card className="bg-zinc-900/50 border-white/10 p-8 mb-8">
          <p className="text-zinc-300 mb-3">{t('deleteAccount.whatDeleted')}</p>
          <ul className="list-disc list-inside space-y-2 ml-4 text-zinc-300">
            <li>{t('deleteAccount.li1')}</li>
            <li>{t('deleteAccount.li2')}</li>
            <li>{t('deleteAccount.li3')}</li>
            <li>{t('deleteAccount.li4')}</li>
          </ul>
        </Card>

        {user ? (
          <Card className="bg-zinc-900/50 border-white/10 p-8">
            <label className="flex items-start gap-3 mb-6 cursor-pointer">
              <input
                type="checkbox"
                checked={confirmed}
                onChange={(e) => setConfirmed(e.target.checked)}
                className="mt-1 h-5 w-5 accent-[#F9004D]"
              />
              <span className="text-zinc-200">{t('deleteAccount.confirmLabel')}</span>
            </label>
            <button
              onClick={() => setStep(1)}
              disabled={!confirmed}
              className="inline-flex items-center gap-2 px-6 py-3 rounded-lg bg-[#F9004D] text-white font-semibold disabled:opacity-40 disabled:cursor-not-allowed hover:bg-[#d80042] transition-colors"
            >
              <Trash2 className="h-5 w-5" />
              {t('deleteAccount.deleteBtn')}
            </button>
          </Card>
        ) : (
          <Card className="bg-zinc-900/50 border-white/10 p-8">
            <p className="text-zinc-300 mb-4">{t('deleteAccount.loggedOut')}</p>
            <button
              onClick={() => navigate('/login')}
              className="inline-flex items-center gap-2 px-6 py-3 rounded-lg bg-white/10 text-white font-semibold hover:bg-white/20 transition-colors"
            >
              {t('deleteAccount.loginBtn')}
            </button>
            <p className="text-zinc-400 mt-6">{t('deleteAccount.contactIntro')}</p>
            <p className="text-[#00F0FF]">contact@guidematch-tokyo.com</p>
          </Card>
        )}
      </div>

      {/* Bandeaux de confirmation (double sécurité) */}
      {step > 0 && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm px-4">
          <Card className="bg-zinc-900 border-[#F9004D]/50 p-8 max-w-md w-full">
            <div className="flex items-start gap-3 mb-4">
              <AlertTriangle className="h-6 w-6 text-[#F9004D] flex-shrink-0 mt-1" />
              <h2 className="text-xl font-bold text-white">
                {step === 1 ? t('deleteAccount.confirm1Title') : t('deleteAccount.confirm2Title')}
              </h2>
            </div>

            {step === 1 ? (
              <p className="text-zinc-300 mb-6">{t('deleteAccount.confirm1Text')}</p>
            ) : (
              <p className="text-[#F9004D] font-semibold mb-6">{t('deleteAccount.confirm2Text')}</p>
            )}

            <div className="flex flex-col sm:flex-row gap-3">
              <button
                onClick={() => { if (!busy) setStep(0); }}
                disabled={busy}
                className="flex-1 px-5 py-3 rounded-lg bg-white/10 text-white font-semibold hover:bg-white/20 transition-colors disabled:opacity-40"
              >
                {t('deleteAccount.cancel')}
              </button>
              {step === 1 ? (
                <button
                  onClick={() => setStep(2)}
                  className="flex-1 px-5 py-3 rounded-lg bg-[#F9004D] text-white font-semibold hover:bg-[#d80042] transition-colors"
                >
                  {t('deleteAccount.confirm1Continue')}
                </button>
              ) : (
                <button
                  onClick={doDelete}
                  disabled={busy}
                  className="flex-1 inline-flex items-center justify-center gap-2 px-5 py-3 rounded-lg bg-[#F9004D] text-white font-semibold hover:bg-[#d80042] transition-colors disabled:opacity-40"
                >
                  {busy ? <Loader2 className="h-5 w-5 animate-spin" /> : <Trash2 className="h-5 w-5" />}
                  {busy ? t('deleteAccount.deleting') : t('deleteAccount.confirm2Delete')}
                </button>
              )}
            </div>
          </Card>
        </div>
      )}
    </div>
  );
}
